import { useState, useEffect } from 'react';
import { api } from '../utils/apiConfig';

/**
 * The measured effect of the changes already applied on one campaign.
 *
 * Feeds the recommendations table's Actual Impact column. The shape is keyed
 * exactly the way the Apply button keys its rows —
 * `byRun[optimizeMessageId][suggestionIndex]` — so the column lands on the row
 * it belongs to without matching on timestamps or restating a key function.
 *
 * DEGRADES TO NULL, ALWAYS. The endpoint sits behind the performance-tab flag,
 * so an account without it gets a 403; a campaign on a platform with no per-day
 * reporting has nothing to measure. Both are "no column", never an error banner
 * on the activity feed — this decorates the page, it does not carry it.
 *
 * REFETCHES WHEN AN APPLY LANDS. `appliedCount` is the caller's count of applied
 * edits; passing it means the column appears after someone clicks Apply without
 * polling for a measurement that cannot exist for days.
 *
 * @param {string} campaignId
 * @param {string} accountId
 * @param {number} [appliedCount] changes to this value trigger a refetch
 * @returns {{byRun: Object|null, isLoading: boolean}}
 */
export function useAppliedImpacts(campaignId, accountId, appliedCount = 0) {
  const [byRun, setByRun] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!campaignId || !accountId) {
      setByRun(null);
      return undefined;
    }
    let live = true;
    setIsLoading(true);
    api.get('/api/v2/performance/applied-impact', {
      params: { accountId, campaignId },
    })
      .then((res) => {
        if (!live) return;
        const next = res?.data?.byRun;
        // An empty object is a real answer: nothing applied yet. Null is "we
        // could not ask", and the two must not collapse into the same render.
        setByRun(next && typeof next === 'object' ? next : null);
      })
      .catch(() => { if (live) setByRun(null); })
      .finally(() => { if (live) setIsLoading(false); });
    return () => { live = false; };
  }, [campaignId, accountId, appliedCount]);

  return { byRun, isLoading };
}

export default useAppliedImpacts;
